import useTakeSnapshot from 'pages/Editor/sections/Flow/hooks/useTakeSnapshot';
import { useCallback } from 'react';
import { Box, Node, NodeOrigin, Rect, boxToRect, getNodePositionWithOrigin, rectToBox, useStore } from 'reactflow';

type IsEqualCompareObj = {
  minWidth: number;
  minHeight: number;
  hasChildNodes: boolean;
};

const isEqual = (prev: IsEqualCompareObj, next: IsEqualCompareObj): boolean => {
  return (
    prev.minWidth === next.minWidth && prev.minHeight === next.minHeight && prev.hasChildNodes === next.hasChildNodes
  );
};

const getRelativeNodesBounds = (nodes: Node[], nodeOrigin: NodeOrigin = [0, 0]): Rect => {
  if (nodes.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const getBoundsOfBoxes = (box1: Box, box2: Box): Box => ({
    x: Math.min(box1.x, box2.x),
    y: Math.min(box1.y, box2.y),
    x2: Math.max(box1.x2, box2.x2),
    y2: Math.max(box1.y2, box2.y2),
  });

  const box = nodes.reduce(
    (currBox, node) => {
      const { x, y } = getNodePositionWithOrigin(node, nodeOrigin);
      return getBoundsOfBoxes(
        currBox,
        rectToBox({
          x,
          y,
          width: node.width || 0,
          height: node.height || 0,
        })
      );
    },
    { x: Infinity, y: Infinity, x2: -Infinity, y2: -Infinity }
  );

  return boxToRect(box);
};

interface UseNodeResizeParams {
  id: string;
}

const useNodeResize = ({ id }: UseNodeResizeParams) => {
  const { minWidth, minHeight } = useStore((store) => {
    const childNodes = Array.from(store.nodeInternals.values()).filter((n) => n.parentNode === id);
    const rect = getRelativeNodesBounds(childNodes);

    return {
      minWidth: rect.x + rect.width,
      minHeight: rect.y + rect.height,
      hasChildNodes: childNodes.length > 0,
    };
  }, isEqual);
  const { takeSnapshot } = useTakeSnapshot();

  const handleResizeStart = useCallback(() => {
    takeSnapshot();
  }, [takeSnapshot]);

  return {
    minWidth,
    minHeight,
    onResizeStart: handleResizeStart,
  };
};

export default useNodeResize;
